// =====================================================
// BÚSQUEDA DE PRODUCTOR - CORREGIDO
// src/pages/Agro/BusquedaProductor/index.js
// =====================================================
// FIX: El input perdía el foco porque MainContent estaba
// definido DENTRO del componente, causando que se recreara
// en cada cambio de estado.
//
// SOLUCIÓN: Mover todo el contenido directamente al return
// o usar useMemo/useCallback para estabilizar referencias.
// =====================================================

import React, { useState, useCallback, useEffect, useMemo, useRef } from "react"

import RequireRole from "components/RequireRole"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  InputGroup,
  Label,
  Spinner,
  Badge,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap"
import { useHistory } from "react-router-dom"
import DataTable from "react-data-table-component"
import { textosPaginacion } from "components/Tables/paginacion"
import PaginacionServidor from "components/Tables/PaginacionServidor"
import classnames from "classnames"

import Layout from "components/Layout"

import { buscarProductoresPaginado, getProductorByCuit } from "api/agro"
import toastHandler from "toastHandler"
import { formatearCuit } from "helpers/formatters"
import { getErrorMessage, isCancelledError } from "helpers/error_helpers"
import {
  ORDENES,
  SENTIDOS,
  LIMITES,
  TAMANIOS_PAGINA,
  OPCIONES_INICIALES,
  formatearEntero,
  resumenOpciones,
} from "./opcionesBusqueda"

const ID_PANEL = "opciones-busqueda-panel"
const ID_TOGGLE = "opciones-busqueda-toggle"

const SIN_RESULTADO = {
  contenido: [],
  total: 0,
  totalCoincidencias: 0,
  limite: 0,
  limiteAplicado: false,
}

const BusquedaProductor = ({ permissionBy }) => {
  const history = useHistory()
  const { setError, setSuccess } = toastHandler()

  const [loading, setLoading] = useState(false)
  const [resultado, setResultado] = useState(SIN_RESULTADO)
  const [searchMode, setSearchMode] = useState("nombre") // nombre | cuit
  const [searchQuery, setSearchQuery] = useState("")
  const [searched, setSearched] = useState(false)
  const [errorBusqueda, setErrorBusqueda] = useState("")
  // BDSJEA-444: opciones (orden, limite, tamanio, pagina). El estado alimenta la pantalla y el ref evita valores viejos
  // en los callbacks de la tabla; el panel recuerda abierto/cerrado solo mientras la pantalla esta montada.
  const [params, setParams] = useState(OPCIONES_INICIALES)
  const paramsRef = useRef(OPCIONES_INICIALES)
  const [panelAbierto, setPanelAbierto] = useState(false)
  // Texto y limite de la ultima busqueda ejecutada: los cambios de pagina/tamanio/orden recargan con ellos.
  const consultaRef = useRef(null)
  // Limite de la ultima consulta (estado, para repintar el resumen); null si todavia no hubo busqueda por nombre.
  const [limiteVigente, setLimiteVigente] = useState(null)
  // Cambia en cada busqueda nueva: reinicia la pagina interna de la tabla.
  const [generacion, setGeneracion] = useState(0)
  // Respuestas atrasadas: solo vale la ultima solicitud.
  const solicitudRef = useRef(0)
  const abortRef = useRef(null)

  useEffect(
    () => () => {
      solicitudRef.current += 1
      if (abortRef.current) abortRef.current.abort()
    },
    [],
  )

  const aplicarParams = nuevos => {
    paramsRef.current = nuevos
    setParams(nuevos)
  }

  const handleCopiarCuit = async cuit => {
    try {
      await navigator.clipboard.writeText(String(cuit || "").replace(/\D/g, ""))
      setSuccess("CUIT copiado")
    } catch {
      setError("No se pudo copiar el CUIT")
    }
  }

  // Columnas de la tabla. BDSJEA-444: en modo nombre el orden lo hace el servidor; sortField es el valor de "orden"
  // del contrato (los mismos del combo «Ordenar por»). Establecimientos y Acciones no se ordenan.
  const servidor = searchMode === "nombre"
  const columns = useMemo(
    () => [
      {
        id: "cuit",
        sortField: "cuit",
        name: "CUIT",
        selector: row => row.cuit,
        cell: row => (
          <div className="d-flex align-items-center gap-1">
            <span>{formatearCuit(row.cuit)}</span>
            <Button
              color="link"
              size="sm"
              className="p-0"
              title="Copiar CUIT"
              onClick={() => handleCopiarCuit(row.cuit)}
            >
              <i className="mdi mdi-content-copy" />
            </Button>
          </div>
        ),
        sortable: servidor,
        width: "160px",
      },
      {
        id: "nombre",
        sortField: "nombre",
        name: "Razón Social",
        selector: row => row.razonSocial,
        sortable: servidor,
        grow: 2,
      },
      {
        id: "tipo",
        sortField: "tipo",
        name: "Tipo",
        selector: row => row.tipoProductor,
        sortable: servidor,
        cell: row => (
          <Badge
            color={
              row.tipoProductor === "GRANDE"
                ? "danger"
                : row.tipoProductor === "MEDIANO"
                  ? "warning"
                  : "info"
            }
          >
            {row.tipoProductor || "N/D"}
          </Badge>
        ),
      },
      {
        id: "establecimientos",
        name: "Establecimientos",
        selector: row => row.cantidadEstablecimientos,
        sortable: false,
        center: true,
        width: "140px",
      },
      {
        id: "acciones",
        name: "Acciones",
        cell: row => (
          <Button
            color="primary"
            size="sm"
            onClick={() => history.push(`/agro/productor/${row.id}`)}
          >
            Ver
          </Button>
        ),
        width: "100px",
        center: true,
      },
    ],
    // handleCopiarCuit solo usa setters estables del store
    [servidor, history],
  )

  // Pide una pagina al servidor. Descarta las respuestas viejas (contador) y cancela la solicitud anterior.
  const cargar = useCallback(async (consulta, p) => {
    const id = ++solicitudRef.current
    if (abortRef.current) abortRef.current.abort()
    const ctrl =
      typeof AbortController !== "undefined" ? new AbortController() : null
    abortRef.current = ctrl
    setLoading(true)
    setErrorBusqueda("")
    try {
      const data = await buscarProductoresPaginado(
        {
          q: consulta.q,
          page: p.pagina,
          size: p.size,
          limite: consulta.limite,
          orden: p.orden,
          sentido: p.sentido,
        },
        { signal: ctrl ? ctrl.signal : undefined },
      )
      if (id !== solicitudRef.current) return
      const contenido = Array.isArray(data?.contenido) ? data.contenido : []
      setResultado({
        contenido,
        total: Number.isFinite(data?.total) ? data.total : contenido.length,
        totalCoincidencias: Number.isFinite(data?.totalCoincidencias)
          ? data.totalCoincidencias
          : contenido.length,
        limite: Number.isFinite(data?.limite) ? data.limite : 0,
        limiteAplicado: Boolean(data?.limiteAplicado),
      })
    } catch (err) {
      if (id !== solicitudRef.current || isCancelledError(err)) return
      // El error limpia la lista y se muestra siempre el mensaje descriptivo de la API.
      setResultado(SIN_RESULTADO)
      // Solo la alerta en linea (con el mensaje descriptivo de la API): un toast ademas duplicaba el aviso.
      setErrorBusqueda(getErrorMessage(err))
    } finally {
      if (id === solicitudRef.current) setLoading(false)
    }
  }, [])

  // Buscar productores - useCallback para evitar recreación
  const handleSearch = useCallback(async () => {
    // Por CUIT hace falta un valor exacto; por nombre se puede buscar sin texto (BDSJEA-444: trae todos, paginado y
    // limitado por «Límite de resultados»).
    if (searchMode === "cuit" && !searchQuery.trim()) return

    setSearched(true)
    setErrorBusqueda("")
    setGeneracion(g => g + 1)

    if (searchMode === "cuit") {
      consultaRef.current = null
      setLimiteVigente(null)
      const id = ++solicitudRef.current
      if (abortRef.current) abortRef.current.abort()
      setLoading(true)
      try {
        // Buscar por CUIT exacto
        const productor = await getProductorByCuit(
          searchQuery.replace(/\D/g, ""),
        )
        if (id !== solicitudRef.current) return
        const contenido = productor ? [productor] : []
        setResultado({ ...SIN_RESULTADO, contenido, total: contenido.length })
      } catch (err) {
        if (id !== solicitudRef.current) return
        setResultado(SIN_RESULTADO)
        if (err.response?.status !== 404) {
          setErrorBusqueda(getErrorMessage(err))
        }
      } finally {
        if (id === solicitudRef.current) setLoading(false)
      }
      return
    }

    const nuevos = { ...paramsRef.current, pagina: 0 }
    aplicarParams(nuevos)
    consultaRef.current = { q: searchQuery.trim(), limite: nuevos.limite }
    setLimiteVigente(nuevos.limite)
    await cargar(consultaRef.current, nuevos)
  }, [searchQuery, searchMode, cargar])

  // Cambio de pagina/tamanio/orden con resultados ya mostrados: recarga con el mismo texto y limite.
  const recargar = nuevos => {
    aplicarParams(nuevos)
    if (consultaRef.current) cargar(consultaRef.current, nuevos)
  }

  const handleChangePage = page => {
    const pagina = page - 1
    if (pagina === paramsRef.current.pagina) return
    recargar({ ...paramsRef.current, pagina })
  }

  const handleChangeRowsPerPage = nuevoSize => {
    if (nuevoSize === paramsRef.current.size) return
    recargar({ ...paramsRef.current, size: Number(nuevoSize), pagina: 0 })
  }

  // Encabezado ordenable de la tabla -> mismos valores que el combo «Ordenar por».
  const handleSort = (column, direction) => {
    const orden = column?.sortField
    if (!orden) return
    if (
      orden === paramsRef.current.orden &&
      direction === paramsRef.current.sentido
    )
      return
    recargar({ ...paramsRef.current, orden, sentido: direction, pagina: 0 })
  }

  // Combos de Opciones: orden, sentido y tamanio recargan si ya hay resultados; el limite espera a «Buscar».
  const handleOrden = e =>
    recargar({ ...paramsRef.current, orden: e.target.value, pagina: 0 })
  const handleSentido = e =>
    recargar({ ...paramsRef.current, sentido: e.target.value, pagina: 0 })
  const handleSize = e =>
    recargar({ ...paramsRef.current, size: Number(e.target.value), pagina: 0 })
  const handleLimite = e =>
    aplicarParams({ ...paramsRef.current, limite: Number(e.target.value) })

  const reiniciarBusqueda = () => {
    solicitudRef.current += 1
    if (abortRef.current) abortRef.current.abort()
    consultaRef.current = null
    setLimiteVigente(null)
    setSearchQuery("")
    setResultado(SIN_RESULTADO)
    setSearched(false)
    setErrorBusqueda("")
    setLoading(false)
  }

  // Manejar Enter en el input
  const handleKeyPress = useCallback(
    e => {
      if (e.key === "Enter") {
        e.preventDefault()
        handleSearch()
      }
    },
    [handleSearch],
  )

  // Manejar cambio en el input - solo actualiza el estado
  const handleInputChange = useCallback(e => {
    setSearchQuery(e.target.value)
  }, [])

  // El resumen muestra los valores VIGENTES de la ultima consulta: el limite solo cambia al volver a buscar.
  const opcionesVigentes =
    limiteVigente === null ? params : { ...params, limite: limiteVigente }
  const cambiosSinAplicar =
    limiteVigente !== null && params.limite !== limiteVigente

  // Breadcrumbs para el Layout
  const breadcrumbItems = [
    { title: "Agropecuario", link: "/agro" },
    { title: "Búsqueda de Productor", link: "#" },
  ]

  return (
    <Layout
      title="Búsqueda de Productor"
      breadcrumbItems={breadcrumbItems}
      permissionBy={permissionBy}
      components={[
        {
          component: (
            <>
              <Row>
                <Col xs={12}>
                  <Card>
                    <CardBody>
                      {/* Tabs de modo de búsqueda */}
                      <style>
                        {`
                        .nav-tabs-custom .nav-link.active {
                          color: #495057 !important;
                          background-color: #fff !important;
                          font-weight: 600 !important;
                          border-color: #dee2e6 #dee2e6 #fff !important;
                        }
                        .nav-tabs-custom .nav-link {
                          color: #6c757d !important;
                          cursor: pointer;
                        }
                        .opciones-busqueda {
                          border: 1px solid #dee2e6;
                          border-radius: 0.25rem;
                        }
                        .opciones-busqueda-toggle {
                          display: flex;
                          align-items: flex-start;
                          gap: 0.35rem;
                          width: 100%;
                          padding: 0.5rem 0.75rem;
                          background: transparent;
                          border: 0;
                          text-align: left;
                          color: inherit;
                          overflow-wrap: anywhere;
                        }
                        .opciones-busqueda-toggle:focus-visible {
                          outline: 2px solid #556ee6;
                          outline-offset: 2px;
                        }
                        .opciones-busqueda-panel {
                          padding: 0.75rem;
                          border-top: 1px solid #dee2e6;
                        }
                        .nav-tabs-custom .nav-link:hover:not(.active) {
                          color: #495057 !important;
                          border-color: #e9ecef #e9ecef #dee2e6 !important;
                        }
                      `}
                      </style>
                      <Nav tabs className="nav-tabs-custom mb-4">
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: searchMode === "nombre",
                            })}
                            onClick={() => {
                              setSearchMode("nombre")
                              reiniciarBusqueda()
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="bx bx-user me-1" />
                            Por Nombre
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames({
                              active: searchMode === "cuit",
                            })}
                            onClick={() => {
                              setSearchMode("cuit")
                              reiniciarBusqueda()
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="bx bx-id-card me-1" />
                            Por CUIT
                          </NavLink>
                        </NavItem>
                      </Nav>

                      {/* Campo de búsqueda */}
                      <Row className="justify-content-center">
                        <Col md={8} lg={6}>
                          <InputGroup>
                            <Input
                              type={searchMode === "cuit" ? "text" : "text"}
                              placeholder={
                                searchMode === "cuit"
                                  ? "Ingrese CUIT (ej: 20123456789)"
                                  : "Ingrese nombre o razón social..."
                              }
                              value={searchQuery}
                              onChange={handleInputChange}
                              onKeyPress={handleKeyPress}
                              maxLength={searchMode === "cuit" ? 11 : 100}
                              autoFocus
                            />
                            <Button
                              color="primary"
                              onClick={handleSearch}
                              disabled={
                                loading ||
                                (searchMode === "cuit" && !searchQuery.trim())
                              }
                            >
                              {loading ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className="bx bx-search" />
                              )}{" "}
                              Buscar
                            </Button>
                          </InputGroup>
                          {searchMode === "cuit" && (
                            <small className="text-muted">
                              Ingrese solo números, sin guiones ni puntos
                            </small>
                          )}
                        </Col>
                      </Row>

                      {/* BDSJEA-444: panel colapsable «Opciones» (cerrado por defecto) con resumen de los valores vigentes */}
                      {searchMode === "nombre" && (
                        <div className="opciones-busqueda mt-3">
                          <button
                            type="button"
                            id={ID_TOGGLE}
                            className="opciones-busqueda-toggle"
                            aria-expanded={panelAbierto}
                            aria-controls={ID_PANEL}
                            onClick={() => setPanelAbierto(abierto => !abierto)}
                          >
                            <i
                              className={`mdi ${panelAbierto ? "mdi-chevron-down" : "mdi-chevron-right"}`}
                              aria-hidden="true"
                            />
                            <span>
                              <strong>Opciones</strong>:{" "}
                              <span className="text-muted">
                                {resumenOpciones(opcionesVigentes)}
                              </span>
                              {cambiosSinAplicar && (
                                <Badge
                                  color="warning"
                                  className="ms-2"
                                  role="status"
                                >
                                  cambios sin aplicar
                                </Badge>
                              )}
                            </span>
                          </button>
                          <div
                            id={ID_PANEL}
                            role="group"
                            aria-labelledby={ID_TOGGLE}
                            hidden={!panelAbierto}
                            className="opciones-busqueda-panel"
                          >
                            <Row className="g-3">
                              <Col xs={12} sm={6} lg={3}>
                                <Label for="opcion-orden" className="mb-1">
                                  Ordenar por
                                </Label>
                                <Input
                                  type="select"
                                  id="opcion-orden"
                                  value={params.orden}
                                  onChange={handleOrden}
                                >
                                  {ORDENES.map(o => (
                                    <option key={o.valor} value={o.valor}>
                                      {o.etiqueta}
                                    </option>
                                  ))}
                                </Input>
                              </Col>
                              <Col xs={12} sm={6} lg={3}>
                                <Label for="opcion-sentido" className="mb-1">
                                  Sentido
                                </Label>
                                <Input
                                  type="select"
                                  id="opcion-sentido"
                                  value={params.sentido}
                                  onChange={handleSentido}
                                >
                                  {SENTIDOS.map(o => (
                                    <option key={o.valor} value={o.valor}>
                                      {o.etiqueta}
                                    </option>
                                  ))}
                                </Input>
                              </Col>
                              <Col xs={12} sm={6} lg={3}>
                                <Label for="opcion-limite" className="mb-1">
                                  Límite de resultados
                                </Label>
                                <Input
                                  type="select"
                                  id="opcion-limite"
                                  value={params.limite}
                                  onChange={handleLimite}
                                >
                                  {LIMITES.map(o => (
                                    <option key={o.valor} value={o.valor}>
                                      {o.etiqueta}
                                    </option>
                                  ))}
                                </Input>
                              </Col>
                              <Col xs={12} sm={6} lg={3}>
                                <Label for="opcion-size" className="mb-1">
                                  Resultados por página
                                </Label>
                                <Input
                                  type="select"
                                  id="opcion-size"
                                  value={params.size}
                                  onChange={handleSize}
                                >
                                  {TAMANIOS_PAGINA.map(n => (
                                    <option key={n} value={n}>
                                      {n}
                                    </option>
                                  ))}
                                </Input>
                              </Col>
                            </Row>
                            <small className="text-muted d-block mt-2">
                              El orden, el sentido y los resultados por página
                              se aplican al instante si ya hay resultados; el
                              límite se aplica en la próxima búsqueda.
                            </small>
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Resultados */}
              {searched && (
                <Row className="mt-3">
                  <Col xs={12}>
                    <Card>
                      <CardBody>
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                          <h5 className="mb-0">
                            {`${formatearEntero(resultado.total)} ${resultado.total === 1 ? "resultado" : "resultados"}`}
                          </h5>
                          {/* BDSJEA-35: un usuario de solo consulta no ve el
                              boton. El alta igual esta protegida en el backend. */}
                          <RequireRole
                            objeto="/agro/busqueda/productor"
                            accion="modificacion"
                          >
                            <Button
                              color="success"
                              size="sm"
                              onClick={() =>
                                history.push("/agro/alta-productor")
                              }
                            >
                              <i className="bx bx-plus me-1" />
                              Nuevo Productor
                            </Button>
                          </RequireRole>
                        </div>

                        {errorBusqueda && (
                          <div className="alert alert-danger py-2" role="alert">
                            {errorBusqueda}
                          </div>
                        )}

                        {resultado.limiteAplicado && (
                          // div de Bootstrap y no <Alert>: el Fade de reactstrap emite un warning de prop-types
                          <div
                            className="alert alert-warning py-2"
                            role="status"
                          >
                            {`Mostrando los primeros ${formatearEntero(resultado.limite)} de ${formatearEntero(resultado.totalCoincidencias)} coincidencias; ajustá el límite en Opciones`}
                          </div>
                        )}

                        {resultado.contenido.length > 0 || loading ? (
                          <DataTable
                            key={`${generacion}-${params.orden}-${params.sentido}-${params.size}`}
                            columns={columns}
                            data={resultado.contenido}
                            highlightOnHover
                            striped
                            responsive
                            noDataComponent="No se encontraron productores"
                            progressPending={loading}
                            progressComponent={
                              <div className="text-center py-4" role="status">
                                <Spinner size="sm" /> Buscando productores...
                              </div>
                            }
                            persistTableHead
                            {...(servidor
                              ? {
                                  pagination: true,
                                  paginationServer: true,
                                  paginationTotalRows: resultado.total,
                                  paginationPerPage: params.size,
                                  paginationDefaultPage: params.pagina + 1,
                                  paginationRowsPerPageOptions: TAMANIOS_PAGINA,
                                  paginationComponent: PaginacionServidor,
                                  paginationComponentOptions: textosPaginacion,
                                  onChangePage: handleChangePage,
                                  onChangeRowsPerPage: handleChangeRowsPerPage,
                                  sortServer: true,
                                  onSort: handleSort,
                                  defaultSortFieldId: columns.some(
                                    c => c.id === params.orden,
                                  )
                                    ? params.orden
                                    : undefined,
                                  defaultSortAsc: params.sentido !== "desc",
                                }
                              : {})}
                          />
                        ) : (
                          !errorBusqueda && (
                            <div className="text-center py-4">
                              <i
                                className="bx bx-search-alt text-muted"
                                style={{ fontSize: "3rem" }}
                              />
                              <p className="text-muted mt-2">
                                No se encontraron productores con los criterios
                                ingresados
                              </p>
                              {/* BDSJEA-239: mismo permiso que el boton
                                "Nuevo Productor" de arriba. */}
                              <RequireRole
                                objeto="/agro/busqueda/productor"
                                accion="modificacion"
                              >
                                <Button
                                  color="primary"
                                  outline
                                  onClick={() =>
                                    history.push("/agro/productor/nuevo")
                                  }
                                >
                                  Crear nuevo productor
                                </Button>
                              </RequireRole>
                            </div>
                          )
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
            </>
          ),
        },
      ]}
    />
  )
}

export default BusquedaProductor
